import clsx from "clsx";
import React, { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
// Helpers
import { getObjectLink } from "@/Common/helpers/getObjectLink";
// MUI
import { Button, Paper, SvgIcon, CircularProgress, Grid } from "@mui/material";
import { generateWashoutsPDF } from "../../../Washouts/api/common/queries";
import { ILocationState } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingSection";
import { SnackbarsActions, SnackbarsContent } from "../Snackbars";
import { SnackbarKey, useSnackbar } from "notistack";
import { ReactComponent as pdfIcon } from "@/assets/icons/svg/pdf.svg";
import { getMappedPosition } from "@/Common/api/positions/queries";

type TReportButtonProps = {
	startIcon?: ReactNode;
	to?: string;
	fileName?: string;
	target?: string;
	sxButton?: Object;
	disabled?: boolean;
	title?: string;
	sx?: Object;
	position: string;
	washoutId: string;
};

const getRenderComponent = (hasLink = false) => (hasLink ? "a" : "button");
const getTarget = (hasLink = false, target = "_blank") => (hasLink ? "_blank" : target);

export default function ReportButtonLinkProgress(props: Readonly<TReportButtonProps>) {
	const { enqueueSnackbar } = useSnackbar();
	const [loading, setLoading] = useState(false);
	const isExternalLink = props?.to?.startsWith("http") ?? false;
	const position = props?.position;
	const washoutId = props?.washoutId;
	const location = useSelector((state: ILocationState) => state.location.location);
	const locationId = location?.locationId;

	const generateAndDownloadPDF = async (event: React.MouseEvent) => {
		setLoading(true);
		try {
			const mappedPosition = await getMappedPosition(locationId, position);
			if (!mappedPosition) {
				throw new Error("Mapped position not found");
			}
			const url = await generateWashoutsPDF(locationId, position, mappedPosition.isDepartmental, washoutId);
			if (url && url?.url?.length > 0) {
				event.preventDefault();
				const blobUrl = await getObjectLink(url?.url, "application/pdf");
				const link = document.createElement("a");
				link.href = blobUrl ?? "";
				link.download = props?.fileName ?? "file.pdf";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				throw new Error("Failed to generate PDF URL");
			}
		} catch (error) {
			showSnackBar("Error Generating Washout PDF.");
		} finally {
			setLoading(false);
		}
	};

	const showSnackBar = (message: string) => {
		enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
			variant: "error",
			preventDuplicate: true,
			action: getAction,
		});
	};

	const getAction = (key: SnackbarKey) => {
		return <SnackbarsActions key={key} index={key} />;
	};

	return (
		<Paper
			className={clsx({
				"[&>a>span]:!text-[22px] [&>a>span]:!ml-0 [&>a>span]:!mr-[5px]": true,
			})}
			elevation={0}
			sx={props.sx || null}
		>
			{loading ? (
				<Grid display={"flex"} className="w-32 !justify-center  mt-1">
					<CircularProgress className="!text-primary mt-1 mb-1" size={20} />
				</Grid>
			) : (
				<Button
					className="!font-sans !capitalize text-[12.5px]"
					variant="text"
					startIcon={props.startIcon ?? <SvgIcon component={pdfIcon} />}
					component={getRenderComponent(isExternalLink)}
					target={getTarget(isExternalLink, props.target)}
					sx={props.sxButton || null}
					disabled={props.disabled || false}
					onClick={generateAndDownloadPDF}
				>
					{props.title ?? "Download PDF"}
				</Button>
			)}
		</Paper>
	);
}
